import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
    Badge,
    Button,
    DataTable,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Skeleton,
    Switch,
    Text,
} from "@athena/ui";
import { Archive, ArchiveRestore, Check, ImageIcon, Pencil, Plus, X } from "lucide-react";
import { BrandsDocument, CreateBrandDocument, UpdateBrandDocument } from "@/graphql/brands.generated";
import type { BrandsQuery } from "@/graphql/brands.generated";
import { ImageUploader } from "@/components/ImageUploader";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";

type Brand = BrandsQuery["brands"][number];

/**
 * Brand management.
 *
 * Two things the UI deliberately does not offer:
 *  - **Editing a slug.** It is the identifier the API sends and what clients cache, so
 *    changing one is a coordinated act rather than an edit. The backend has no field for it.
 *  - **Deleting a brand.** Users and content reference brands; archiving retires a brand
 *    without breaking the rows already pointing at it.
 */
export function BrandsPanel() {
    const { t } = useTranslation();
    const [
        newName,
        setNewName,
    ] = useState<string>("");
    // Which row is being renamed, and the in-flight value. One at a time keeps the table
    // readable and avoids a save-all button that could half-fail.
    const [
        editingId,
        setEditingId,
    ] = useState<string | null>(null);
    const [
        editingName,
        setEditingName,
    ] = useState<string>("");
    /*
     * The brand whose logo is being edited — its presence is the dialog's open state, so the two
     * cannot disagree and the dialog always has a brand to talk about.
     */
    const [
        logoBrand,
        setLogoBrand,
    ] = useState<Brand | null>(null);

    // Archived brands are shown here — this is the screen where you would restore one.
    const { data, loading, refetch } = useQuery(BrandsDocument, { variables: { includeArchived: true } });

    const onError = (error: unknown) => {
        Sentry.captureException(error);
        toast.error(error instanceof Error ? error.message : t("admin.data.brandSaveFailed"));
    };

    const [
        createBrand,
        { loading: creating },
    ] = useMutation(CreateBrandDocument, {
        onError,
        onCompleted: () => {
            setNewName("");
            void refetch();
        },
    });

    // No refetch: the mutation returns the brand, and Apollo's normalized cache updates
    // the row from it. Only a create needs the list re-read, since a new brand is not in
    // the previous result.
    const [updateBrand] = useMutation(UpdateBrandDocument, {
        onError,
        onCompleted: () => setEditingId(null),
    });

    const brands = data?.brands ?? [];

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const view = useListView(brands);
    // Re-read from the list so the dialog reflects an upload that just completed.
    const editingLogoBrand = brands.find((brand) => brand.id === logoBrand?.id) ?? logoBrand;

    /*
     * Stores the managed record rather than the URL the uploader also hands back. `Brand.logoUrl`
     * resolves the asset on read, and an image URL minted now would be the wrong thing to persist —
     * it is re-signed per request.
     */
    const saveLogo = (id: string, field: "imageAssetId" | "imageAssetIdDark", imageId: string | null) =>
        void updateBrand({ variables: { id, input: { [field]: imageId } } });

    const submitNew = () => {
        const name = newName.trim();
        if (!name) return;
        void createBrand({ variables: { input: { name } } });
    };

    const submitRename = (id: string) => {
        const name = editingName.trim();
        if (!name) return;
        void updateBrand({ variables: { id, input: { name } } });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="max-w-sm flex-1">
                    <label className="mb-1 block text-sm font-medium" htmlFor="new-brand-name">
                        {t("admin.data.newBrandLabel")}
                    </label>
                    <Input
                        id="new-brand-name"
                        value={newName}
                        placeholder={t("admin.data.newBrandPlaceholder")}
                        onChange={(event) => setNewName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") submitNew();
                        }}
                        type="text"
                    />
                </div>
                <Button onClick={submitNew} disabled={!newName.trim() || creating}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t("admin.data.addBrand")}
                </Button>
            </div>
            {/* The slug is derived from the name, so say so rather than surprising them. */}
            <Text className="text-muted-foreground text-sm">{t("admin.data.slugDerivedHint")}</Text>

            {loading ? (
                <Skeleton className="h-40 w-full" />
            ) : (
                <div className="overflow-x-auto">
                    <ListViewControls view={view} noun="brands" />

                    <DataTable
                        columns={[
                            {
                                key: "name",
                                header: t("admin.data.columnName"),
                                cell: (brand) =>
                                    editingId === brand.id ? (
                                        <Input
                                            aria-label={t("admin.data.renameBrandLabel")}
                                            value={editingName}
                                            autoFocus
                                            onChange={(event) => setEditingName(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") submitRename(brand.id);
                                                if (event.key === "Escape") setEditingId(null);
                                            }}
                                            type="text"
                                        />
                                    ) : (
                                        brand.name
                                    ),
                            },
                            {
                                key: "logo",
                                header: t("admin.data.columnLogo"),
                                cell: (brand) =>
                                    brand.logoUrl ? (
                                        <img
                                            src={brand.logoUrl}
                                            alt={brand.name}
                                            className="h-6 w-auto max-w-24 object-contain"
                                        />
                                    ) : (
                                        <Text className="text-muted-foreground text-xs">
                                            {t("admin.data.brandLogoNone")}
                                        </Text>
                                    ),
                            },
                            {
                                key: "slug",
                                header: t("admin.data.columnSlug"),
                                cell: (brand) => <code className="text-muted-foreground text-xs">{brand.slug}</code>,
                            },
                            {
                                key: "seesAll",
                                header: t("admin.data.columnSeesAll"),
                                cell: (brand) => (
                                    <Switch
                                        checked={brand.seesAllContent}
                                        aria-label={t("admin.data.seesAllToggleLabel", { name: brand.name })}
                                        onCheckedChange={(checked) =>
                                            void updateBrand({
                                                variables: { id: brand.id, input: { seesAllContent: checked } },
                                            })
                                        }
                                    />
                                ),
                            },
                            {
                                key: "status",
                                header: t("admin.data.columnStatus"),
                                cell: (brand) => (
                                    <Badge variant={brand.archivedAt ? "secondary" : "default"}>
                                        {brand.archivedAt ? t("admin.data.archived") : t("admin.data.active")}
                                    </Badge>
                                ),
                            },
                        ]}
                        rows={view.rows}
                        rowKey={(brand) => brand.id}
                        actionsHeader={t("admin.data.columnActions")}
                        empty={<Text className="text-muted-foreground">{t("admin.data.noBrands")}</Text>}
                        actions={(brand) => {
                            const archived = Boolean(brand.archivedAt);
                            return editingId === brand.id ? (
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        size="icon-sm"
                                        onClick={() => submitRename(brand.id)}
                                        disabled={!editingName.trim()}
                                    >
                                        <Check className="h-4 w-4" />
                                    </Button>
                                    <Button size="icon-sm" variant="outline" onClick={() => setEditingId(null)}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        size="icon-sm"
                                        variant="outline"
                                        title="Edit"
                                        onClick={() => {
                                            setEditingId(brand.id);
                                            setEditingName(brand.name);
                                        }}
                                    >
                                        <Pencil className="text-muted-foreground h-3 w-3" />
                                    </Button>
                                    <Button
                                        size="icon-sm"
                                        variant="outline"
                                        title={t("admin.data.brandLogoLabel")}
                                        aria-label={t("admin.data.brandLogoTitle", { name: brand.name })}
                                        onClick={() => setLogoBrand(brand)}
                                    >
                                        <ImageIcon className="text-muted-foreground h-3 w-3" />
                                    </Button>
                                    <Button
                                        size="icon-sm"
                                        variant="outline"
                                        title={archived ? "Restore" : "Archive"}
                                        onClick={() =>
                                            void updateBrand({
                                                variables: { id: brand.id, input: { archived: !archived } },
                                            })
                                        }
                                    >
                                        {archived ? (
                                            <ArchiveRestore className="text-muted-foreground h-3 w-3" />
                                        ) : (
                                            <Archive className="text-muted-foreground h-3 w-3" />
                                        )}
                                    </Button>
                                </div>
                            );
                        }}
                    />
                </div>
            )}

            <Dialog open={logoBrand !== null} onOpenChange={(open) => !open && setLogoBrand(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {t("admin.data.brandLogoTitle", { name: editingLogoBrand?.name ?? "" })}
                        </DialogTitle>
                        <DialogDescription>{t("admin.data.brandLogoDescription")}</DialogDescription>
                    </DialogHeader>

                    {editingLogoBrand && (
                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col gap-2">
                                <Text className="text-sm font-medium">{t("admin.data.brandLogoLight")}</Text>
                                <ImageUploader
                                    shape="square"
                                    currentUrl={editingLogoBrand.logoUrl}
                                    label={
                                        editingLogoBrand.logoUrl
                                            ? t("admin.data.brandLogoReplace")
                                            : t("admin.data.brandLogoUpload")
                                    }
                                    hint={t("admin.data.brandLogoLightHint")}
                                    onUploaded={(_key, imageId) =>
                                        saveLogo(editingLogoBrand.id, "imageAssetId", imageId)
                                    }
                                    onRemoved={() => saveLogo(editingLogoBrand.id, "imageAssetId", null)}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Text className="text-sm font-medium">{t("admin.data.brandLogoDark")}</Text>
                                <ImageUploader
                                    shape="square"
                                    currentUrl={editingLogoBrand.logoUrlDark}
                                    label={
                                        editingLogoBrand.logoUrlDark
                                            ? t("admin.data.brandLogoReplace")
                                            : t("admin.data.brandLogoUpload")
                                    }
                                    hint={t("admin.data.brandLogoDarkHint")}
                                    onUploaded={(_key, imageId) =>
                                        saveLogo(editingLogoBrand.id, "imageAssetIdDark", imageId)
                                    }
                                    onRemoved={() => saveLogo(editingLogoBrand.id, "imageAssetIdDark", null)}
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLogoBrand(null)}>
                            {t("admin.data.brandLogoDone")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
